/**
 * Token storage for the cerberus access + refresh tokens.
 *
 * Storage keys:
 *   cerberus_access            — short-lived JWT (15min TTL)
 *   cerberus_refresh           — long-lived opaque token (30d TTL)
 *   cerberus_user_id           — opaque user id, required for /auth/refresh
 *   cerberus_active_org_id     — currently selected org (UI state)
 *
 * sessionStorage is used (never localStorage) so tokens are scoped to the
 * browser tab/window and auto-cleared when the browser closes — reduces the
 * blast radius of a stolen-device scenario. Cross-tab sync is provided by
 * the `storage` event listener in useAuth.
 */
export const ACCESS_KEY = 'cerberus_access'
const REFRESH_KEY = 'cerberus_refresh'
const USER_ID_KEY = 'cerberus_user_id'
export const ACTIVE_ORG_KEY = 'cerberus_active_org_id'

export interface StoredTokens {
  access_token: string
  refresh_token: string
  user_id: string
}

function safeStore(): Storage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    return sessionStorage
  } catch {
    return null
  }
}

function safeGet(key: string): string | null {
  try {
    return safeStore()?.getItem(key) ?? null
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    safeStore()?.setItem(key, value)
  } catch {
    /* quota / private mode */
  }
}

function safeRemove(key: string): void {
  try {
    safeStore()?.removeItem(key)
  } catch {
    /* noop */
  }
}

export function getAccessToken(): string | null {
  return safeGet(ACCESS_KEY)
}

export function getRefreshToken(): string | null {
  return safeGet(REFRESH_KEY)
}

export function getUserId(): string | null {
  return safeGet(USER_ID_KEY)
}

export function getActiveOrgId(): string | null {
  return safeGet(ACTIVE_ORG_KEY)
}

export function setTokens(args: StoredTokens): void {
  safeSet(ACCESS_KEY, args.access_token)
  safeSet(REFRESH_KEY, args.refresh_token)
  safeSet(USER_ID_KEY, args.user_id)
}

export function setActiveOrgId(orgId: string | null): void {
  if (orgId === null) {
    safeRemove(ACTIVE_ORG_KEY)
  } else {
    safeSet(ACTIVE_ORG_KEY, orgId)
  }
}

export function clearTokens(): void {
  safeRemove(ACCESS_KEY)
  safeRemove(REFRESH_KEY)
  safeRemove(USER_ID_KEY)
  safeRemove(ACTIVE_ORG_KEY)
}
